import {
  Box,
  Button,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { ProgressStatus } from "../../../services/api/types/general/homeAssignment";
import { brand } from "../../../theme/themePrimitives";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const CalendarIcon = () => (
  <CalendarTodayOutlinedIcon sx={{ color: "#344054", fontSize: "15px" }} />
);

export interface TrainingRecordFormValues {
  status: string;
  score: string;
  startDate: Dayjs | null;
  completionDate: Dayjs | null;
  maxAttempts: number | undefined;
}

interface TrainingRecordFormProps {
  isViewOnly?: boolean;
  isSubmitting?: boolean;
  onSubmit: (values: TrainingRecordFormValues) => void;
  initialValues?: Partial<TrainingRecordFormValues>;
}

const selectSx = {
  borderRadius: "8px",
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    padding: "8.5px 14px",
  },
};

const datePickerSlotProps = (
  placeholder: string,
  error?: boolean,
  helperText?: string,
  required?: boolean,
) => ({
  textField: {
    size: "small" as const,
    fullWidth: true,
    placeholder,
    error,
    helperText,
    required,
    sx: { "& .MuiOutlinedInput-root": { borderRadius: "8px" } },
  },
  openPickerButton: {
    sx: {
      border: "none",
      background: "transparent",
      "&:hover": { background: "transparent" },
    },
  },
});

const sanitizeScore = (val: any): string => {
  if (
    val == null ||
    val === "" ||
    (typeof val === "number" && isNaN(val)) ||
    val === "NaN"
  )
    return "";
  return String(val).replace(/[^0-9]/g, "");
};

const TrainingRecordForm = ({
  onSubmit,
  initialValues,
  isViewOnly,
  isSubmitting,
}: TrainingRecordFormProps) => {
  const [status, setStatus] = useState(initialValues?.status ?? "");
  const [score, setScore] = useState(sanitizeScore(initialValues?.score));
  const [startDate, setStartDate] = useState<Dayjs | null>(
    initialValues?.startDate ?? null,
  );
  const [completionDate, setCompletionDate] = useState<Dayjs | null>(
    initialValues?.completionDate ?? null,
  );
  const [maxAttempts, setMaxAttempts] = useState<number | undefined>(
    initialValues?.maxAttempts,
  );
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const completionDateMissing =
    status === ProgressStatus.COMPLETED && !completionDate;

  useEffect(() => {
    setStatus(initialValues?.status ?? "");
    setScore(sanitizeScore(initialValues?.score));
    setStartDate(initialValues?.startDate ?? null);
    setCompletionDate(initialValues?.completionDate ?? null);
    setMaxAttempts(initialValues?.maxAttempts);
  }, [initialValues]);

  const handleSubmit = () => {
    setAttemptedSubmit(true);
    if (completionDateMissing) return;
    onSubmit({ status, score, startDate, completionDate, maxAttempts: maxAttempts ?? 0 });
  };

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
        <Stack spacing={1}>
          <Typography variant="subtitle2">Status</Typography>
          <Select
            IconComponent={ExpandMoreIcon}
            displayEmpty
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            renderValue={(val) =>
              val ? (
                val
              ) : (
                <span style={{ color: "#9e9e9e" }}>Select status</span>
              )
            }
            sx={selectSx}
            disabled={isViewOnly}
          >
            {Object.values(ProgressStatus).map((s) => (
              <MenuItem key={s} value={s}>
                {s.replace(/-/g, " ").replace(/\(/g, " (")}
              </MenuItem>
            ))}
          </Select>
        </Stack>

        <Stack spacing={1}>
          <Typography variant="subtitle2">Score</Typography>
          <TextField
            placeholder="Enter score"
            value={score}
            onChange={(e) => setScore(e.target.value.replace(/[^0-9]/g, ""))}
            slotProps={{ htmlInput: { inputMode: "numeric", min: 0 } }}
            size="small"
            sx={{ "& .MuiInputBase-input": { px: "14px", py: "10px" } }}
            disabled={isViewOnly}
          />
        </Stack>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">Start date</Typography>
            <DatePicker
              value={startDate}
              disabled={isViewOnly}
              maxDate={completionDate ?? undefined}
              onChange={(value) => {
                const newStart = value as Dayjs | null;
                setStartDate(newStart);
                if (
                  newStart &&
                  completionDate &&
                  completionDate.isBefore(newStart)
                ) {
                  setCompletionDate(null);
                }
              }}
              slots={{ openPickerIcon: CalendarIcon }}
              slotProps={datePickerSlotProps(
                "Pick a start date",
                undefined,
                undefined,
                status === ProgressStatus.COMPLETED,
              )}
            />
          </Stack>
        </LocalizationProvider>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">Completion date</Typography>
            <DatePicker
              value={completionDate}
              disabled={isViewOnly}
              minDate={startDate ?? undefined}
              onChange={(value) => setCompletionDate(value as Dayjs | null)}
              slots={{ openPickerIcon: CalendarIcon }}
              slotProps={datePickerSlotProps(
                "Pick a completion date",
                attemptedSubmit && completionDateMissing,
                attemptedSubmit && completionDateMissing
                  ? "Completion date is required when status is completed"
                  : undefined,
                status === ProgressStatus.COMPLETED,
              )}
            />
          </Stack>
        </LocalizationProvider>

        <Stack spacing={1}>
          <Typography variant="subtitle2">Max no. attempts</Typography>
          <TextField
            placeholder="Enter max attempts"
            type="number"
            value={maxAttempts ?? ""}
            onChange={(e) => {
              const sanitized = e.target.value.replace(/[^0-9]/g, "");
              setMaxAttempts(sanitized === "" ? undefined : Number(sanitized));
            }}
            onKeyDown={(e) =>
              ["-", "+", "e", "E", "."].includes(e.key) && e.preventDefault()
            }
            slotProps={{ htmlInput: { min: 0 } }}
            size="small"
            sx={{ "& .MuiInputBase-input": { px: "14px", py: "10px" } }}
          />
        </Stack>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting}
          sx={{
            backgroundColor: brand[600],
            borderRadius: "12px",
            color: "white",
            p: "10px 16px",
            "&:hover": {
              backgroundColor: brand[700],
            },
            "&.Mui-disabled": {
              backgroundColor: brand[300],
              color: "white",
            },
          }}
        >
          {isSubmitting ? "Updating..." : "Update"}
        </Button>
      </Box>
    </Paper>
  );
};

export default TrainingRecordForm;
